import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Copy, Check, MessageSquare, ExternalLink, Search,
  HelpCircle, Clock, Users, Target, Brain, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { PageLayout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

// Forum snippets - ready to copy answers
const forumSnippets = [
  {
    id: 1,
    question: "What are the pass rates for train driver psychometric tests?",
    category: "Pass Rates",
    icon: Target,
    answer: `Based on RSSB research, the pass rates vary but here are the key statistics:

• First-time pass rate: typically 20-40%
• Assessment centre pass rate: around 41%
• Average applicants per vacancy: 317

The main reasons candidates fail include:
- Unfamiliarity with test formats
- Poor time management under pressure  
- Inconsistent concentration during longer tests
- Underestimating the difficulty

Practice definitely helps - studies show candidates can improve scores by 20-30% with proper preparation. The key is getting familiar with the test formats before your actual assessment.`,
    hashtags: ['#TrainDriver', '#PsychometricTests', '#UKRail'],
    forumSuitable: ['RailUK', 'Reddit', 'Facebook Groups'],
  },
  {
    id: 2,
    question: "Can I become a train driver at 18?",
    category: "Age Requirements",
    icon: Users,
    answer: `Yes, you can now become a train driver at 18 in the UK!

The minimum age was traditionally 20, but RSSB research found that experience matters more than age. Key findings:

• No correlation between younger drivers and SPAD rates
• Train driving's structured environment suits younger workers  
• Multiple safety systems provide adequate protection
• The EU Train Driver Directive already allows 18-year-olds

The selection process and psychometric tests are the same regardless of age. What matters is:
- Passing the assessments
- Meeting medical standards
- Completing the training programme

If you're 18 and interested, start practicing the psychometric tests now and apply when positions are advertised.`,
    hashtags: ['#TrainDriverCareer', '#YoungDrivers', '#Apprenticeship'],
    forumSuitable: ['RailUK', 'Reddit', 'Careers Forums'],
  },
  {
    id: 3,
    question: "I have ADHD/autism - can I still become a train driver?",
    category: "Neurodiversity",
    icon: Brain,
    answer: `Absolutely! Many successful train drivers are neurodivergent. RSSB research actually found that the train driver role can be well-suited to neurodivergent individuals because:

**Strengths neurodivergent people often bring:**
• Exceptional attention to detail
• Strong pattern recognition
• Ability to hyperfocus
• Preference for rule-based tasks
• Consistency and reliability

**Why the role may suit you:**
• Fixed timetables and predictable routines
• Clear rules and procedures
• Limited social interaction in the cab
• Technical, systematic work

**For the assessments:**
• You may be entitled to reasonable adjustments (extra time, separate room, etc.)
• Practice extensively to build familiarity
• Many psychometric tests actually measure skills where neurodivergent individuals excel

An estimated 15% of the UK population is neurodivergent - you're not alone, and your differences can be genuine strengths in this role.`,
    hashtags: ['#Neurodiversity', '#ADHD', '#Autism', '#TrainDriver', '#Inclusion'],
    forumSuitable: ['RailUK', 'Reddit', 'Neurodiversity Forums'],
  },
  {
    id: 4,
    question: "How do I prepare for the Group Bourdon test?",
    category: "Test Preparation",
    icon: Target,
    answer: `The Group Bourdon test measures your concentration and sustained attention. Here's how to prepare:

**What the test involves:**
• A large grid of dot patterns
• You identify cells with exactly 4 dots
• Ignore patterns with 1, 2, 3, or 5 dots
• Timed sections (usually 2 minutes each)

**How to practice:**
1. Start with short sessions (5-10 mins) and build up
2. Focus on accuracy first, then speed
3. Develop a systematic scanning pattern (left to right, top to bottom)
4. Practice under timed conditions
5. Take short breaks between practice sections

**Tips for the actual test:**
• Don't rush - accuracy matters more than completing everything
• If you miss one, keep moving - don't go back
• Maintain steady focus throughout
• Get good rest the night before

RSSB research noted that the Bourdon test has "inconsistent validity" but it's still widely used, so practice is essential.`,
    hashtags: ['#GroupBourdon', '#ConcentrationTest', '#TrainDriverTests'],
    forumSuitable: ['RailUK', 'Reddit'],
  },
  {
    id: 5,
    question: "What happens if I fail the psychometric tests?",
    category: "Retakes & Rules",
    icon: HelpCircle,
    answer: `If you fail train driver psychometric tests, here's what typically happens:

**Immediate impact:**
• You cannot proceed with that application
• Most TOCs require a waiting period of 6-12 months before reapplying

**Retake rules (vary by company):**
• Some TOCs limit total attempts (often 2-3 maximum)
• Some share results across the industry
• Failing multiple times may affect future applications elsewhere

**What you can do:**
1. Ask for feedback if available
2. Identify which tests you struggled with
3. Practice extensively during the waiting period
4. Consider whether any factors affected your performance (stress, illness, etc.)
5. Reapply when you're genuinely ready

**Good news:**
RSSB research suggests allowing candidates multiple re-sits (up to 3) because performance can improve with practice and it shows commitment.

The key is using any waiting period productively - dedicated practice can improve scores by 20-30%.`,
    hashtags: ['#TrainDriverTests', '#Retakes', '#CareerAdvice'],
    forumSuitable: ['RailUK', 'Reddit', 'Facebook Groups'],
  },
  {
    id: 6,
    question: "What cognitive skills do train driver tests measure?",
    category: "Test Types",
    icon: Brain,
    answer: `Train driver psychometric tests measure the cognitive abilities needed for safe train operation. Based on RSSB research, here are the key skills assessed:

**Attention & Concentration:**
• Sustained attention over long periods
• Selective attention (focus on relevant info)
• Divided attention (monitoring multiple things)

**Vigilance:**
• Detecting rare but important events
• Maintaining alertness during monotonous periods

**Reaction Time:**
• Speed of response to stimuli
• Accuracy under time pressure

**Memory:**
• Working memory capacity
• Recall of procedures and routes

**Perceptual Skills:**
• Visual processing speed
• Pattern recognition
• Spatial awareness

**Decision Making:**
• Prioritisation under pressure
• Rule application

The tests include things like concentration grids (Bourdon), reaction time tasks (DTG), and trainability assessments (TRP). The TRP test - which measures ability to learn and apply rules - is considered the most valid predictor of success.`,
    hashtags: ['#CognitiveTests', '#TrainDriverAssessment', '#Psychometrics'],
    forumSuitable: ['RailUK', 'Reddit', 'Psychology Forums'],
  },
  {
    id: 7,
    question: "How long should I practice before my assessment?",
    category: "Preparation Timeline",
    icon: Clock,
    answer: `Based on research and candidate experiences, here's a recommended preparation timeline:

**Minimum recommendation: 2-4 weeks**
• Daily practice of 30-60 minutes
• Cover all test types
• Build up to full-length practice sessions

**Ideal preparation: 4-8 weeks**
• More time to identify weak areas
• Practice under realistic conditions
• Build concentration stamina

**What to focus on:**

**Weeks 1-2:**
• Learn what each test involves
• Try each test type at least once
• Identify your strengths and weaknesses

**Weeks 3-4:**
• Focus on weaker areas
• Increase session length
• Practice under timed conditions

**Final week:**
• Full practice sessions
• Simulate test conditions
• Rest well (don't cram!)

**Key points:**
• Consistent daily practice beats occasional long sessions
• Quality matters more than quantity
• Build concentration gradually
• Get proper sleep the night before

Research shows practice can improve scores by 20-30% - the investment is worth it.`,
    hashtags: ['#TestPreparation', '#TrainDriverCareer', '#StudyTips'],
    forumSuitable: ['RailUK', 'Reddit', 'Careers Forums'],
  },
  {
    id: 8,
    question: "What is RIS-3751-TOM and why does it matter?",
    category: "Official Standard",
    icon: Shield,
    answer: `RIS-3751-TOM is the official Rail Industry Standard that governs ALL train driver psychometric assessments in the UK. Here's why it matters:

**What it is:**
• Published by RSSB (Rail Safety and Standards Board)
• Current version is Issue 4 (December 2024)
• Every TOC must follow this standard
• Defines exactly what must be assessed

**The 9 aptitudes it requires testing:**
1. Selective Attention (Group Bourdon test)
2. Divided Attention (TEA-Occ test)
3. Vigilance (WAFV test)
4. Memory (TRP tests)
5. Reasoning
6. Perception (ATAVT test)
7. Reaction Time
8. Hand Coordination (2HAND test)
9. Communication

**Key rules from the standard:**
• Maximum 3 attempts at assessment
• Results valid for 5 years
• Waiting periods required between attempts
• Reasonable adjustments must be available

Understanding this standard helps you know exactly what you're preparing for.`,
    hashtags: ['#RIS3751TOM', '#TrainDriverAssessment', '#RSSB', '#UKRail'],
    forumSuitable: ['RailUK', 'Reddit', 'Careers Forums'],
  },
  {
    id: 9,
    question: "What are the 9 aptitudes tested in train driver assessments?",
    category: "Test Types",
    icon: Brain,
    answer: `According to RIS-3751-TOM (the official standard), these are the 9 aptitudes every train driver candidate must be assessed on:

**1. Selective Attention**
Focusing on relevant info while ignoring distractions (e.g., identifying the right signal)

**2. Divided Attention**
Handling multiple information sources simultaneously (e.g., track + instruments + radio)

**3. Vigilance**
Staying alert for rare events over long periods (e.g., unexpected hazards)

**4. Memory**
Learning and recalling procedures, routes, and rules

**5. Reasoning**
Problem-solving and decision-making (e.g., fault diagnosis)

**6. Perception**
Judging speed, distance, and anticipating situations

**7. Reaction Time**
Quick, accurate responses to stimuli

**8. Hand Coordination**
Smooth, controlled movements (e.g., operating controls)

**9. Communication**
Clear verbal and written communication

Each aptitude has specific tests - practice them all for the best chance of success.`,
    hashtags: ['#TrainDriverTests', '#9Aptitudes', '#RIS3751TOM', '#Assessment'],
    forumSuitable: ['RailUK', 'Reddit', 'Facebook Groups'],
  },
  {
    id: 10,
    question: "How long are train driver psychometric test results valid?",
    category: "Rules & Regulations",
    icon: Clock,
    answer: `According to RIS-3751-TOM (the official industry standard):

**Results are valid for 5 years**

What this means for you:

**If you pass:**
• You don't need to retake for 5 years
• Results can be transferred between TOCs
• If you don't get a position immediately, your results remain valid
• After 5 years, you'd need to reassess

**If you fail:**
• Failed attempts are also recorded
• You have max 3 attempts total
• Waiting periods required between attempts
• Results (including failures) can be shared between TOCs

**Important implications:**
• Don't waste attempts by applying unprepared
• If you pass but don't get hired, apply elsewhere while results are valid
• Plan your applications strategically
• TOCs can request your assessment history

The 5-year validity gives you flexibility, but make your attempts count!`,
    hashtags: ['#TrainDriverTests', '#ResultsValidity', '#RIS3751TOM'],
    forumSuitable: ['RailUK', 'Reddit', 'Careers Forums'],
  },
  {
    id: 11,
    question: "Can I request reasonable adjustments for the psychometric tests?",
    category: "Accessibility",
    icon: Users,
    answer: `Yes! RIS-3751-TOM requires TOCs to make reasonable adjustments for candidates who need them.

**Who can request adjustments:**
• Candidates with disabilities
• Neurodivergent candidates (ADHD, autism, dyslexia, etc.)
• Those with medical conditions affecting test performance

**Types of adjustments available:**
• Extra time for assessments
• Separate/quieter testing room
• Breaks during testing
• Modified test formats where possible
• Clearer instructions
• Assistive technology

**How to request:**
1. Inform the TOC when applying
2. Provide evidence if required (diagnosis, letter from GP/specialist)
3. Discuss specific needs with the assessment team
4. Adjustments arranged before your test date

**Important notes:**
• Adjustments don't lower the required standard
• Safety-critical requirements still apply
• This is about removing barriers, not changing what's assessed
• It's your legal right under the Equality Act 2010

Don't be afraid to ask - it's about giving you a fair opportunity to demonstrate your abilities.`,
    hashtags: ['#ReasonableAdjustments', '#Accessibility', '#Neurodiversity', '#EqualityAct'],
    forumSuitable: ['RailUK', 'Reddit', 'Disability Forums', 'Neurodiversity Forums'],
  },
];

// Community links
const communityLinks = [
  {
    name: 'RailUK Forums',
    description: 'The largest UK rail enthusiast community with active train driver discussion threads',
    url: 'https://www.railforums.co.uk/',
    category: 'Forum',
  },
  {
    name: 'Reddit r/uktrains',
    description: 'UK trains subreddit with career advice threads',
    url: 'https://www.reddit.com/r/uktrains/',
    category: 'Reddit',
  },
  {
    name: 'Train Driver Foundation',
    description: 'Official TDF website with career resources',
    url: 'https://www.traindriverfoundation.com/',
    category: 'Official',
  },
];

export const ForumSnippetsPage = () => {
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(forumSnippets.map(s => s.category))];

  const filteredSnippets = forumSnippets.filter(snippet => {
    const matchesSearch = snippet.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          snippet.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || snippet.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const copyToClipboard = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <PageLayout>
      <SEO 
        title="Forum Snippets | Ready-to-Share Train Driver Answers"
        description="Expert answers to common train driver questions. Copy and share on forums, Reddit, and social media. Based on official RSSB research."
        canonical="/resources/forum-snippets"
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0B1D51] to-[#1a3a7a] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-[#FF9F1C] text-white">
              <MessageSquare className="w-3 h-3 mr-1" />
              Community Resource
            </Badge>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Forum Snippets
            </h1>
            <p className="text-lg text-white/80">
              Expert answers to common train driver questions. Copy and share to help fellow aspiring drivers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? 'bg-[#0B1D51]' : ''}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Snippets */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {filteredSnippets.map((snippet) => (
              <motion.div
                key={snippet.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="overflow-hidden">
                  <CardHeader className="bg-[#0B1D51]/5 border-b">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#0B1D51] rounded-lg flex items-center justify-center flex-shrink-0">
                          <snippet.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <Badge variant="outline" className="text-xs mb-2">
                            {snippet.category}
                          </Badge>
                          <CardTitle className="text-lg text-[#0B1D51]">
                            {snippet.question}
                          </CardTitle>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(snippet.id, `Q: ${snippet.question}\n\n${snippet.answer}\n\nFor more info: https://traindriverpsychometrics.co.uk/resources`)}
                        className="flex-shrink-0"
                      >
                        {copiedId === snippet.id ? (
                          <>
                            <Check className="w-4 h-4 mr-1 text-green-600" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1" />
                            Copy Answer
                          </>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="whitespace-pre-line text-[#334155] text-sm leading-relaxed">
                      {snippet.answer}
                    </div>
                    <div className="mt-6 pt-4 border-t flex flex-wrap items-center gap-4">
                      <div className="flex flex-wrap gap-2">
                        {snippet.hashtags.map((tag) => (
                          <span key={tag} className="text-xs text-[#0B1D51] bg-[#0B1D51]/5 px-2 py-1 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs text-[#64748B]">
                        Good for: {snippet.forumSuitable.join(', ')}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Links */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-6">
            Where to Share
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {communityLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card className="h-full hover:shadow-md transition-shadow group">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge variant="outline" className="text-xs mb-2">
                          {link.category}
                        </Badge>
                        <h3 className="font-semibold text-[#0B1D51] group-hover:text-[#FF9F1C] transition-colors">
                          {link.name}
                        </h3>
                        <p className="text-sm text-[#64748B] mt-1">
                          {link.description}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-[#64748B] group-hover:text-[#FF9F1C] transition-colors flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Guidelines */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-[#0B1D51] text-white">
            <CardContent className="p-8">
              <h3 className="font-heading text-xl font-bold mb-4">
                Sharing Guidelines
              </h3>
              <ul className="space-y-3 text-white/80">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
                  <span>Feel free to adapt the answers to fit the specific question being asked</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
                  <span>Include the link back to our resources for more detailed information</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
                  <span>Be helpful and supportive - we're all here to help aspiring train drivers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
                  <span>These answers are based on official RSSB research and industry standards</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </PageLayout>
  );
};

export default ForumSnippetsPage;
